import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Brain, Sparkles, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import PhoneFrame from "../components/PhoneFrame.jsx";
import api, { API_URL } from "../lib/api.js";
import {
  isSpeechRecognitionSupported, isSpeechSynthesisSupported,
  createRecognizer, speak, speakWithPersona, stopSpeaking,
} from "../lib/voice.js";

const SUGGESTIONS = [
  "How am I doing this week?",
  "Suggest a 10-min posture routine",
  "Tips for desk posture",
  "How do I earn more Posture Points?",
];

export default function Coach() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("ps_voice") === "1");
  const recRef = useRef(null);
  const endRef = useRef(null);
  const spokenRef = useRef(new Set());
  const sttSupported = isSpeechRecognitionSupported();
  const ttsSupported = isSpeechSynthesisSupported();

  useEffect(() => {
    api.get("/coach/history").then((r) => {
      const hist = r.data;
      setMessages(hist);
      // Auto-fetch today's proactive nudge if there's no nudge in history yet today
      const todayIso = new Date().toISOString().slice(0, 10);
      const hasTodayNudge = hist.some(
        (m) => m.role === "assistant" && m.kind === "nudge" && (m.created_at || "").startsWith(todayIso)
      );
      if (!hasTodayNudge) {
        api.post("/coach/nudge")
          .then((res) => {
            setMessages((prev) => [
              ...prev,
              {
                id: `nudge-${todayIso}`,
                role: "assistant",
                kind: "nudge",
                text: res.data.text,
                created_at: new Date().toISOString(),
              },
            ]);
          })
          .catch(() => {});
      }
    });
    return () => { stopSpeaking(); try { recRef.current?.stop(); } catch (e) {} };
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  useEffect(() => {
    if (!voiceOn || streaming || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== "assistant" || !last.text) return;
    const key = `${messages.length}-${last.text.slice(0, 24)}`;
    if (spokenRef.current.has(key)) return;
    spokenRef.current.add(key);
    // Use the persona's OpenAI voice; falls back to browser TTS on failure.
    const base = (process.env.REACT_APP_BACKEND_URL || "");
    speakWithPersona(last.text, { apiBase: base });
  }, [streaming, voiceOn, messages]);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("ps_voice", next ? "1" : "0");
    if (!next) stopSpeaking();
  };

  const toggleListen = () => {
    if (!sttSupported) return toast.error("Voice input not supported in this browser");
    if (listening) { try { recRef.current?.stop(); } catch (e) {} return; }
    const rec = createRecognizer({
      onResult: ({ final, interim }) => {
        if (final) setText((t) => (t + " " + final).trim());
        else if (interim) setText(interim);
      },
      onError: (e) => { toast.error(`Voice: ${e.error || "error"}`); setListening(false); },
      onEnd: () => setListening(false),
    });
    if (!rec) return toast.error("Voice not available");
    recRef.current = rec;
    setListening(true);
    try { rec.start(); } catch (e) { setListening(false); }
  };

  const send = async (msg) => {
    const userMsg = msg ?? text;
    if (!userMsg.trim() || streaming) return;
    setText("");
    setMessages((m) => [...m, { role: "user", text: userMsg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const token = localStorage.getItem("ps_token");
      const resp = await fetch(`${API_URL}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: userMsg }),
      });
      if (!resp.body) throw new Error("no stream");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(ln.slice(6));
            if (ev.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + ev.delta };
                return copy;
              });
            }
          } catch (e) {}
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry — I had trouble connecting. Try again in a moment." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  return (
    <PhoneFrame>
      <div className="ps-aurora flex flex-col h-screen">
        <div className="px-5 pt-6 pb-4 border-b border-[#1B364A] flex items-center gap-3 relative">
          <button onClick={() => nav(-1)} className="text-slate-400" data-testid="coach-back"><ChevronLeft size={20} /></button>
          <div className="relative w-11 h-11 rounded-2xl bg-gradient-to-br from-[#a78bfa]/40 to-[#4ade80]/20 border border-[#a78bfa]/30 flex items-center justify-center">
            <Brain size={20} color="#a78bfa" />
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#4ade80] border-2 border-[#050E15] ps-pulse" />
          </div>
          <div>
            <div className="ps-heading font-extrabold text-lg leading-none">AI Wellness Coach</div>
            <div className="text-[10px] text-[#a78bfa] uppercase tracking-[0.25em] font-bold mt-1">
              Powered by Claude
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {ttsSupported && (
              <button
                data-testid="coach-toggle-voice"
                onClick={toggleVoice}
                aria-label={voiceOn ? "Mute coach voice" : "Unmute coach voice"}
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${voiceOn ? "bg-[#4ade80] text-[#0a1822] border-transparent shadow-[0_0_18px_rgba(74,222,128,0.4)]" : "bg-[#15303E] border-[#1B364A] text-slate-300"}`}
              >
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="text-center py-12 ps-fade-up">
              <Sparkles size={32} className="mx-auto text-[#4ade80]" />
              <div className="ps-heading text-2xl mt-4 font-extrabold">
                Hi. <span className="ps-serif ps-italic font-normal ps-gradient-text">I'm your coach.</span>
              </div>
              <p className="text-slate-400 text-sm mt-3 max-w-[260px] mx-auto leading-relaxed ps-serif ps-italic">
                Ask me anything about posture, walking, recovery or your progress.
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id || `${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} ps-fade-up`}>
              {m.role === "assistant" && (
                <div className="w-7 h-7 rounded-full mr-2 self-end mb-1 bg-gradient-to-br from-[#a78bfa]/40 to-[#4ade80]/20 border border-[#a78bfa]/30 flex items-center justify-center shrink-0">
                  <Brain size={12} color="#a78bfa" />
                </div>
              )}
              <div className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-gradient-to-br from-[#4ade80] to-[#34d399] text-[#022014] rounded-2xl rounded-br-md font-medium shadow-[0_8px_20px_-8px_rgba(74,222,128,0.5)]"
                  : `ps-glass rounded-2xl rounded-bl-md ${m.kind === "nudge" ? "border-[#4ade80]/40" : "border-[#a78bfa]/15"}`
              }`}>
                {m.role === "assistant" && m.kind === "nudge" && (
                  <div className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded-full bg-[#4ade80]/15 border border-[#4ade80]/40 text-[9px] uppercase tracking-widest font-bold text-[#4ade80]" data-testid="nudge-badge">
                    <Sparkles size={9} /> Today's nudge
                  </div>
                )}
                {m.text || (streaming && i === messages.length - 1 ? <span className="ps-pulse text-slate-400">●●●</span> : "")}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {messages.length === 0 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                data-testid={`coach-suggestion-${s.slice(0, 5).replace(/\s+/g, "-")}`}
                className="text-xs px-3 py-2 rounded-full border border-[#1B364A] bg-[#15303E]/60 backdrop-blur text-slate-300 hover:border-[#4ade80] hover:text-white transition-colors ps-serif ps-italic"
              >{s}</button>
            ))}
          </div>
        )}

        <div className="p-3 border-t border-[#1B364A] flex gap-2 items-center bg-[#050E15]/80 backdrop-blur">
          {sttSupported ? (
            <button
              data-testid="coach-mic"
              onClick={toggleListen}
              aria-label={listening ? "Stop listening" : "Start voice input"}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${listening ? "bg-[#EF4444] text-white animate-pulse shadow-[0_0_20px_rgba(239,68,68,0.6)]" : "bg-[#15303E] border border-[#1B364A] text-slate-300 hover:border-[#4ade80]"}`}
            >
              {listening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          ) : (
            <button
              data-testid="coach-mic-unsupported"
              onClick={() => toast("Voice input requires Chrome, Edge or Brave on this device.")}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-[#15303E] border border-[#1B364A] text-slate-500"
            >
              <Mic size={18} />
            </button>
          )}
          <input
            data-testid="coach-input"
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={listening ? "Listening…" : "Ask your coach…"}
            className="flex-1 bg-[#15303E]/60 border border-[#1B364A] rounded-full px-4 py-3 text-sm text-white outline-none focus:border-[#4ade80] focus:shadow-[0_0_0_4px_rgba(74,222,128,0.12)] transition-shadow"
          />
          <button
            data-testid="coach-send"
            disabled={streaming || !text.trim()}
            onClick={() => send()}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4ade80] to-[#34d399] text-[#022014] flex items-center justify-center disabled:opacity-40 shadow-[0_8px_20px_-8px_rgba(74,222,128,0.5)]"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
}
